import _ from 'lodash';
import Injector from 'vue-inject';
import Debug from '@/util/debug';
import { getLocationCountries } from '@/util/helper';
import { MODE_ONEOFF, MODE_SUBSCRIPTION, MODE_MULTI_MONTH } from '@/services/ModeService';

const debug = Debug('service:Cluster');

function clusterFullModeName(cluster) {
  if (cluster.mode === MODE_SUBSCRIPTION && cluster.subscription_duration > 0) {
    return `${MODE_MULTI_MONTH}:${cluster.subscription_duration}`;
  }

  return cluster.mode;
}

export default class ClusterService {
  constructor(aas, ps, gcs) {
    this.adApiService = aas;
    this.packageService = ps;
    this.genericConfigService = gcs;
    this.clusters = null;
    this.subs_count = 1;
    this.error = null;
  }

  loadClustersFromApi() {
    debug.info('loadClustersFromApi');
    return Promise.all([
      this.adApiService.getClusters(),
      this.packageService.getAllPackages(),
      this.genericConfigService.getConfig().then(config => {
        this.subs_count = config.subscription_max_segments;
      }),
    ])
      .then(([clusters, packages]) => clusters.map(c => {
        const cps = packages.filter(p => p.cluster === c.name);

        return {
          ...c,
          packages: cps,
          minReach: cps.reduce((min, p) => Math.min(p.reach.min, min), Number.MAX_VALUE),
          reachDivider: c.mode === MODE_ONEOFF ? 1 : this.getReachDivider(c.goal_type),
          fullModeName: clusterFullModeName(c),
        };
      }))
      .then(clusters => this.clusters = clusters)
      .catch(err => {
        debug.error('error while loading from api: %o', err);
        this.error = err;
        throw err;
      });
  }

  getReachDivider(goalType) {
    if (_.isNumber(this.subs_count)) { // support for old format
      return this.subs_count > 0
        ? parseInt(this.subs_count, 10)
        : 1;
    }

    return _.get(this.subs_count, goalType, 1);
  }

  getClusters() {
    debug.info('getClusters err: %o, clusters: %o', this.error, this.clusters);
    if (this.error !== null) {
      return new Promise((resolve, reject) => reject(this.error));
    } else if (this.clusters) {
      return new Promise(resolve => resolve(this.clusters));
    }

    return this.loadClustersFromApi();
  }

  availableClusters(campaign, forcePackage = false) {
    const countries = getLocationCountries(campaign.target.locations);

    let clusters = this.clusters;

    // By forcing package we find cluster that contains specific package
    // and force cluster min reach to be equal to package min reach
    if (forcePackage) {
      clusters = clusters
        .filter(c => c.packages.findIndex(p => p.name === forcePackage) >= 0)
        .map(c => ({
          ...c,
          minReach: c.packages.find(p => p.name === forcePackage).reach.min,
        }));
    } else {
      clusters = clusters
        .filter(c => !_.has(c, 'max_cpm') || parseFloat(c.max_cpm) > campaign.cpm)
        .filter(c => !_.has(c, 'goal_type') || c.goal_type === campaign.goal_type)
        .filter(c => !_.has(c, 'country_code') || countries.indexOf(c.country_code) >= 0);
    }

    clusters = Object.values(
      _.reduce(
        _.groupBy(clusters, 'fullModeName'),
        (res, modeClusters, fullMode) => ({
          ...res,
          [fullMode]: _.minBy(modeClusters, i => _.get(i, 'max_cpm', Number.MAX_VALUE)),
        }),
        {},
      ),
    );

    return clusters;
  }
}

Injector.service(
  'clusterService',
  ['adApiService', 'packageService', 'genericConfigService'],
  ClusterService,
);



// WEBPACK FOOTER //
// ./src/services/ClusterService.js